import DOMPurify from "dompurify";
import React, { ReactElement, useState } from "react";
import { jsx } from "slate-hyperscript";
import {
    AiOutlineAlignCenter, AiOutlineAlignLeft, AiOutlineAlignRight, AiOutlineBorder, AiOutlineCloseCircle,
} from "react-icons/ai";
import { SiShadow } from "react-icons/si";
import {
    BlockHandler, ElementSettings, ElementType, StateElement, TextElement,
} from "./base";
import Styles from "../Editor.module.scss";

enum AttributeSize {
    SMALL = "sm",
    MEDIUM = "md",
    LARGE = "lg",
}

enum Align {
    LEFT = "left",
    CENTER = "center",
    RIGHT = "right",
}

const BUTTON_CLASS = {
    [AttributeSize.SMALL]: "border-gray-500 border rounded-1",
    [AttributeSize.MEDIUM]: "border-gray-400 border rounded-1",
    [AttributeSize.LARGE]: "border-gray-200 border rounded-1",
};

export interface ImageSettings extends ElementSettings {
    src?: string;
    altText?: string;
    width?: number;
    height?: number;
    shadow?: AttributeSize;
    border?: AttributeSize;
    align?: Align;
}

const SIZE_NEXT = {
    undefined: AttributeSize.SMALL,
    [AttributeSize.SMALL]: AttributeSize.MEDIUM,
    [AttributeSize.MEDIUM]: AttributeSize.LARGE,
    [AttributeSize.LARGE]: undefined,
};

const SHADOW_CLASS = {
    [AttributeSize.SMALL]: "shadow-sm",
    [AttributeSize.MEDIUM]: "shadow",
    [AttributeSize.LARGE]: "shadow-lg",
};

const BORDER_CLASS = {
    [AttributeSize.SMALL]: "border rounded-1",
    [AttributeSize.MEDIUM]: "border rounded-3",
    [AttributeSize.LARGE]: "border rounded-4",
};

const ALIGN_CLASS = {
    [Align.LEFT]: "",
    [Align.CENTER]: "mx-auto",
    [Align.RIGHT]: "ms-auto",
};

const ALIGN_ICON = {
    [Align.LEFT]: <AiOutlineAlignLeft />,
    [Align.CENTER]: <AiOutlineAlignCenter />,
    [Align.RIGHT]: <AiOutlineAlignRight />,
};

const ALIGN_NEXT = {
    [Align.LEFT]: Align.CENTER,
    [Align.CENTER]: Align.RIGHT,
    [Align.RIGHT]: Align.LEFT,
};
BlockHandler.register(ElementType.IMAGE, {
    nodeTypes: ["IMG"],
    toBlock: (child: ReactElement, settings: ImageSettings, editorSettings) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [currentWidth, setWidth] = useState<string | undefined>(settings.width?.toString());
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [currentHeight, setHeight] = useState<string | undefined>(settings.height?.toString());

        const setValue = (value: Record<string, unknown>) => {
            editorSettings.onUpdate({
                type: ElementType.IMAGE,
                [ElementType.IMAGE]: {
                    ...settings,
                    ...value,
                },
                children: [{
                    text: "",
                } as TextElement],
            } as StateElement);
        };
        const resize = (width?: number, height?: number) => {
            setValue({
                width,
                height,
            });
        };

        const styles: {width?: number; height?: number} = {};
        if (settings.width) {
            styles.width = typeof settings.width === "string" ? parseInt(settings.width, 10) : settings.width;
        }
        if (settings.height) {
            styles.height = typeof settings.height === "string" ? parseInt(settings.height, 10) : settings.width;
        }

        return (
            <div
                contentEditable={false}
                className={`position-relative d-block ${Styles["editable-image"]} ${ALIGN_CLASS[settings.align ?? Align.LEFT]}`}
                style={styles}
            >
                {!editorSettings.isReadOnly && (
                    <>
                        <div
                            className={`bg-blue-dark rounded position-absolute top-0
                                        d-flex text-white align-items-center
                                        ${settings.align === Align.RIGHT ? "end-0" : "start-0"}
                                        ${Styles["editable-image-resizer"]}`}
                        >
                            <button
                                className={`btn btn-sm text-white btn-sm py-2 px-2 ${
                                    settings.border ? BUTTON_CLASS[settings.border] : ""}`}
                                type="button"
                                title="Add a border"
                                onClick={() => setValue({ border: SIZE_NEXT[settings.border ?? "undefined"] })}
                            >
                                <AiOutlineBorder />
                            </button>
                            <button
                                className={`btn btn-sm text-white btn-sm py-2 px-2 ms-1 ${
                                    settings.shadow ? BUTTON_CLASS[settings.shadow] : ""}`}
                                type="button"
                                title="Add a shadow"
                                onClick={() => setValue({ shadow: SIZE_NEXT[settings.shadow ?? "undefined"] })}
                            >
                                <SiShadow />
                            </button>
                            <button
                                className="btn btn-sm text-white btn-sm py-2 px-2 ms-1"
                                type="button"
                                title="Add a shadow"
                                onClick={() => setValue({ align: ALIGN_NEXT[settings.align ?? Align.LEFT] })}
                            >
                                {ALIGN_ICON[settings.align ?? Align.LEFT]}
                            </button>

                            <button
                                className="btn btn-sm text-white btn-sm py-1 px-2"
                                type="button"
                                onClick={() => resize(100)}
                            >
                                Small
                            </button>
                            <button
                                className="btn btn-sm text-white btn-sm py-1 px-2"
                                type="button"
                                onClick={() => resize(512)}
                            >
                                Medium
                            </button>
                            <button
                                className="btn btn-sm text-white btn-sm py-1 px-2"
                                type="button"
                                onClick={() => resize(1024)}
                            >
                                Large
                            </button>
                            <small className="ms-5 text-gray-400">Custom</small>
                            <input
                                placeholder="width"
                                className="bg-blue-dark border-0 text-white form-control"
                                type="number"
                                onChange={evt => setWidth(evt.target.value)}
                            />
                            <input
                                placeholder="height"
                                className="bg-blue-dark border-0 text-white form-control"
                                type="number"
                                onChange={evt => setHeight(evt.target.value)}
                            />
                            <button
                                className="btn btn-sm border-blue-light text-white btn-sm py-1 px-2 m-0"
                                type="button"
                                onClick={() => resize(
                                    (!currentWidth || currentWidth === "auto") ? undefined : parseInt(currentWidth, 10),
                                    (!currentHeight || currentHeight === "auto") ? undefined : parseInt(currentHeight, 10),
                                )}
                            >
                                Resize
                            </button>

                        </div>
                        <button
                            className="btn btn-blue-dark btn-sm fs-4 text-white btn-sm py-1 px-2
                            position-absolute top-0 start-100 opacity-50 translate-middle"
                            type="button"
                            onClick={editorSettings.onRemove}
                        >
                            <AiOutlineCloseCircle />
                        </button>
                    </>
                )}
                <img
                    src={settings.src}
                    alt={settings.altText}
                    className={`w-100 h-100 ${
                        settings.shadow ? SHADOW_CLASS[settings.shadow] : ""} ${
                        settings.border ? BORDER_CLASS[settings.border] : ""}`}
                />
                {child}
            </div>
        );
    },
    serialize: (_child, settings: ImageSettings | undefined) => {
        const text = DOMPurify().sanitize(settings?.altText ?? "");
        return `<img src="${DOMPurify().sanitize(settings?.src ?? "")}" `
                    + `alt="${text}" width="${settings?.width?.toString() ?? ""}" `
                    + `height="${settings?.height?.toString() ?? ""}" `
                    + `shadow="${settings?.shadow?.toString() ?? ""}" `
                    + `border="${settings?.border?.toString() ?? ""}" `
                    + `align="${settings?.align?.toString() ?? ""}"`
                    + `>`;
    },
    deserialize: (children, settings) => jsx("element", {
        type: ElementType.IMAGE,
        [ElementType.IMAGE]: {
            src: settings?.getAttribute("src"),
            altText: settings?.getAttribute("alt"),
            height: settings?.getAttribute("height"),
            width: settings?.getAttribute("width"),
            shadow: settings?.getAttribute("shadow"),
            border: settings?.getAttribute("border"),
            align: settings?.getAttribute("align"),
        },
    }, children),

});
